import { RefObject, useCallback, useState } from "react";
import useSpaces from "hooks/useSpaces";
import { createMarker } from "utils/markerUtils";

const useEconomicMarkers = (map: RefObject<mapboxgl.Map | null>) => {
	const spaces = {
		jobCenter: null,
		smallBusiness: null,
		financialEd: null,
		creditUnion: null,
		careerTraining: null,
		workforce: null,
		entrepreneur: null,
	};

	const [economicMarkers, setEconomicMarkers] = useState<{
		[key: string]: mapboxgl.Marker[] | null;
	}>(spaces);

	const [markerPositions, setMarkerPositions] = useState<{
		[key: string]: mapboxgl.LngLat | null;
	}>(spaces);

	const { economicSpaces } = useSpaces();

	const handleEconomicMarkers = useCallback(
		(spaceId: string) => {
			// Change cursor to indicate placement mode
			if (map && map.current) {
				map.current.getCanvas().style.cursor = "crosshair";

				// Add one-time click handler for map
				const clickHandler = (e: mapboxgl.MapMouseEvent) => {
					const spaceConfig = economicSpaces[spaceId];

					if (!spaceConfig) return;
					if (!map.current) return;

					const marker = createMarker(spaceConfig, e.lngLat, map.current);

					// Store marker position
					setMarkerPositions((prev) => ({
						...prev,
						[spaceId]: e.lngLat,
					}));

					// Add dragend event listener to update position
					marker.on("dragend", () => {
						const newPosition = marker.getLngLat();
						setMarkerPositions((prev) => ({
							...prev,
							[spaceId]: newPosition,
						}));
					});

					// Store the marker
					setEconomicMarkers((prev) => ({
						...prev,
						[spaceId]: prev[spaceId]
							? [...(prev[spaceId] as []), marker]
							: [marker],
					}));

					// Reset cursor
					map.current!.getCanvas().style.cursor = "";
					map.current?.off("click", clickHandler);
				};

				map.current.once("click", clickHandler);
			}
		},
		[economicSpaces, map]
	);

	return {
		markerPositions,
		economicMarkers,
		setEconomicMarkers,
		handleEconomicMarkers,
	};
};

export default useEconomicMarkers;
